import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { StageResult } from './types';
import type { SimulationPlaybackFrame } from './simulationPlayback';
import {
  ENGINEERING_CURVE_METRICS,
  availableCurveMetrics,
  buildEngineeringCurveRows,
  buildLiveEngineeringCurveRows,
  type EngineeringCurveMetricId,
  type EngineeringCurveScope,
  type EngineeringCurveRow,
} from './simulationEngineeringCurves';

const BORDER = '#D6DAE0';
const TEXT = '#1E2A3A';
const MUTED = '#708399';
const ALL_METRICS = Object.keys(ENGINEERING_CURVE_METRICS) as EngineeringCurveMetricId[];

type Props = {
  stages: StageResult[];
  liveFrames?: SimulationPlaybackFrame[];
  selectedStage?: number | null;
  currentTimeS?: number;
  onTimeChange?: (timeS: number) => void;
  height?: number | string;
  compact?: boolean;
};

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const exportRows = (rows: EngineeringCurveRow[]) => {
  const headers = ['time_s', 'local_time_s', 'stage', 'cluster', ...ALL_METRICS];
  const body = rows.map((row) => [
    row.timeS, row.localTimeS, row.stageNumber, row.clusterId ?? '',
    ...ALL_METRICS.map((metric) => row[metric]),
  ].map(csvCell).join(','));
  const blob = new Blob([`\uFEFF${[headers.join(','), ...body].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'fracsim-dynamic-curves.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const chartOption = (
  rows: EngineeringCurveRow[],
  metrics: EngineeringCurveMetricId[],
  currentTimeS?: number,
) => {
  const visible = metrics.filter((metric) => rows.some((row) => row[metric] !== null));
  const axes = visible.map((metric, index) => {
    const definition = ENGINEERING_CURVE_METRICS[metric];
    const right = index % 2 === 1;
    const layer = Math.floor(index / 2);
    return {
      type: 'value',
      name: `${definition.label} (${definition.unit})`,
      position: right ? 'right' : 'left',
      offset: layer * 54,
      axisLine: { show: true, lineStyle: { color: definition.color } },
      axisLabel: { color: definition.color, fontSize: 9 },
      nameTextStyle: { color: definition.color, fontSize: 9 },
      splitLine: { show: index === 0, lineStyle: { color: 'rgba(30,42,58,0.08)' } },
      scale: true,
    };
  });
  const sideLayers = Math.max(1, Math.ceil(visible.length / 2));
  return {
    animation: false,
    color: visible.map((metric) => ENGINEERING_CURVE_METRICS[metric].color),
    grid: { left: 50 + (sideLayers - 1) * 54, right: 50 + (sideLayers - 1) * 54, top: 36, bottom: 48 },
    legend: { top: 4, type: 'scroll', textStyle: { fontSize: 10, color: TEXT } },
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'cross' },
      formatter: (params: any[]) => {
        const index = Number(params?.[0]?.dataIndex ?? 0);
        const row = rows[index];
        if (!row) return '';
        return [
          `<b>${(row.timeS / 60).toFixed(2)} min</b> · 第${row.stageNumber}段${row.clusterId ? ` · ${row.clusterId}` : ''}`,
          ...params.map((item) => `${item.marker}${item.seriesName}: ${item.value?.[1] ?? '—'}`),
        ].join('<br/>');
      },
    },
    toolbox: { right: 8, feature: { dataZoom: {}, restore: {} }, iconStyle: { borderColor: MUTED } },
    dataZoom: [{ type: 'inside', xAxisIndex: 0 }, { type: 'slider', height: 14, bottom: 8 }],
    xAxis: {
      type: 'value', name: '时间 (min)', min: 0,
      axisPointer: { show: true, snap: true, handle: { show: true, size: 16, color: '#6A4C93' } },
      axisLabel: { formatter: (value: number) => (value / 60).toFixed(1), fontSize: 9 },
      nameTextStyle: { fontSize: 10 },
    },
    yAxis: axes,
    series: visible.map((metric, index) => ({
      name: ENGINEERING_CURVE_METRICS[metric].label,
      type: 'line', yAxisIndex: index, showSymbol: rows.length < 40, symbolSize: 4,
      connectNulls: false, lineStyle: { width: index < 3 ? 2 : 1.5 },
      data: rows.map((row) => [row.timeS, row[metric]]),
      markLine: currentTimeS === undefined ? undefined : {
        silent: true, symbol: 'none', label: { show: false },
        lineStyle: { color: '#111827', type: 'dashed', width: 1 }, data: [{ xAxis: currentTimeS }],
      },
    })),
  };
};

function CurveChart({ rows, metrics, currentTimeS, onTimeChange, height }: {
  rows: EngineeringCurveRow[];
  metrics: EngineeringCurveMetricId[];
  currentTimeS?: number;
  onTimeChange?: (timeS: number) => void;
  height: number;
}) {
  const option = useMemo(() => chartOption(rows, metrics, currentTimeS), [rows, metrics, currentTimeS]);
  const seek = (event: any) => {
    const value = Number(event?.axesInfo?.[0]?.value ?? event?.value?.[0]);
    if (Number.isFinite(value)) onTimeChange?.(value);
  };
  return <ReactECharts option={option} notMerge style={{ height }} onEvents={{ updateAxisPointer: seek, click: seek }} />;
}

export default function DynamicSimulationCurves({
  stages, liveFrames = [], selectedStage, currentTimeS, onTimeChange, height = '100%', compact = false,
}: Props) {
  const stageNumbers = useMemo(() => {
    const ids = new Set(stages.map((stage) => stage.stage_number));
    liveFrames.forEach((frame) => frame.clusters.forEach((cluster) => ids.add(Number(cluster.stageId))));
    return [...ids].filter(Number.isFinite).sort((a, b) => a - b);
  }, [stages, liveFrames]);
  const initialStage = selectedStage ?? stageNumbers[0];
  const [scopeKind, setScopeKind] = useState<EngineeringCurveScope['kind']>('WELL');
  const [stageNumber, setStageNumber] = useState<number | undefined>(initialStage);
  const clusterIds = useMemo(() => {
    const ids = new Set<string>();
    stages.find((stage) => stage.stage_number === stageNumber)?.clusters?.forEach((cluster) => {
      if (cluster.cluster_id) ids.add(cluster.cluster_id);
    });
    liveFrames.forEach((frame) => frame.clusters
      .filter((cluster) => Number(cluster.stageId) === stageNumber)
      .forEach((cluster) => ids.add(cluster.clusterId)));
    return [...ids];
  }, [stages, liveFrames, stageNumber]);
  const [clusterId, setClusterId] = useState<string | undefined>();
  useEffect(() => {
    if (selectedStage !== null && selectedStage !== undefined && stageNumbers.includes(selectedStage)) {
      setStageNumber(selectedStage);
      setClusterId(undefined);
    } else if (stageNumber === undefined && stageNumbers.length) {
      setStageNumber(stageNumbers[0]);
    }
  }, [selectedStage, stageNumber, stageNumbers.join('|')]);
  const [metrics, setMetrics] = useState<EngineeringCurveMetricId[]>([
    'bottomholePressure', 'pumpRate', 'halfLength', 'height',
  ]);
  const [displayMode, setDisplayMode] = useState<'OVERLAY' | 'SEPARATE'>('OVERLAY');
  const scope: EngineeringCurveScope = {
    kind: scopeKind,
    stageNumber,
    clusterId: clusterId ?? clusterIds[0],
  };
  const completedRows = useMemo(() => buildEngineeringCurveRows(stages, scope), [stages, scopeKind, stageNumber, clusterId, clusterIds.join('|')]);
  const liveRows = useMemo(() => buildLiveEngineeringCurveRows(liveFrames, scope), [liveFrames, scopeKind, stageNumber, clusterId, clusterIds.join('|')]);
  const rows = liveRows.length ? liveRows : completedRows;
  const available = availableCurveMetrics(rows);
  const unavailable = ALL_METRICS.filter((metric) => !available.includes(metric));
  const chartHeight = compact ? 210 : 360;

  return <div style={{ height, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#fff', color: TEXT }}>
    <div style={{ padding: compact ? '5px 8px' : '8px 14px', borderBottom: `1px solid ${BORDER}`, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <strong style={{ fontSize: 11 }}>真实施工与裂缝响应</strong>
      <select value={scopeKind} onChange={(event) => setScopeKind(event.target.value as EngineeringCurveScope['kind'])} style={control}>
        <option value="WELL">全井</option><option value="STAGE">单段</option><option value="CLUSTER">单簇</option>
      </select>
      {scopeKind !== 'WELL' && <select value={stageNumber ?? ''} onChange={(event) => { setStageNumber(Number(event.target.value)); setClusterId(undefined); }} style={control}>
        {stageNumbers.map((number) => <option key={number} value={number}>第 {number} 段</option>)}
      </select>}
      {scopeKind === 'CLUSTER' && <select value={clusterId ?? clusterIds[0] ?? ''} onChange={(event) => setClusterId(event.target.value)} style={control}>
        {clusterIds.map((id) => <option key={id} value={id}>{id}</option>)}
      </select>}
      <select value={displayMode} onChange={(event) => setDisplayMode(event.target.value as 'OVERLAY' | 'SEPARATE')} style={control}>
        <option value="OVERLAY">多纵轴叠加</option><option value="SEPARATE">独立显示</option>
      </select>
      <button type="button" onClick={() => exportRows(rows)} disabled={!rows.length} style={button}>导出 CSV</button>
      <span style={{ fontSize: 10, color: MUTED }}>{liveRows.length ? `实时真实帧 ${liveRows.length}` : `历史真实时间步 ${rows.length}`}</span>
    </div>
    <div style={{ padding: '5px 10px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
      {ALL_METRICS.map((metric) => {
        const definition = ENGINEERING_CURVE_METRICS[metric];
        const enabled = metrics.includes(metric);
        const hasData = available.includes(metric);
        return <label key={metric} title={definition.definition} style={{ fontSize: 10, color: hasData ? TEXT : '#A0AAB6', cursor: hasData ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" disabled={!hasData} checked={enabled && hasData} onChange={(event) => setMetrics((current) => event.target.checked
            ? [...current, metric]
            : current.filter((item) => item !== metric))} style={{ accentColor: definition.color }} />
          {definition.label} <span style={{ color: MUTED }}>({definition.unit})</span>
        </label>;
      })}
    </div>
    {!rows.length ? <div style={empty}>当前范围没有已提交的真实时间步。</div> : metrics.filter((metric) => available.includes(metric)).length === 0
      ? <div style={empty}>请选择至少一条有数据的曲线。</div>
      : <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {displayMode === 'OVERLAY'
          ? <CurveChart rows={rows} metrics={metrics} currentTimeS={currentTimeS} onTimeChange={onTimeChange} height={compact ? chartHeight : Math.max(chartHeight, 420)} />
          : <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(2,minmax(360px,1fr))' }}>
            {metrics.filter((metric) => available.includes(metric)).map((metric) => <CurveChart key={metric} rows={rows} metrics={[metric]} currentTimeS={currentTimeS} onTimeChange={onTimeChange} height={chartHeight} />)}
          </div>}
      </div>}
    {!compact && <div style={{ padding: '5px 10px', borderTop: `1px solid ${BORDER}`, fontSize: 10, color: MUTED }}>
      压力参考：射孔簇入口井筒压力。浓度：泵注程序质量/载液体积，未建模井筒滞后。几何：半长为井筒至翼尖，高度为总缝高，宽度为井筒入口最大动态缝宽。
      {unavailable.length > 0 && ` 缺少有效数据：${unavailable.map((metric) => ENGINEERING_CURVE_METRICS[metric].label).join('、')}。`}
    </div>}
  </div>;
}

const control: React.CSSProperties = { fontSize: 10, padding: '3px 6px', border: `1px solid ${BORDER}`, borderRadius: 3, background: '#fff' };
const button: React.CSSProperties = { ...control, color: TEXT, cursor: 'pointer' };
const empty: React.CSSProperties = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: 12 };
